import React, { Component } from 'react';
import FloatingActionButton from '../../components/FloatingActionButton';
import theme, { applyTheme } from '../../utils/theme';
import { decideABTestGroup, FetchTimeout } from '../../utils/misc';
import { trackingService } from '../../utils/TrackingService';
import { WIDGET_EVENT_PREFIX, WIDGET_EVENTS, HOUR_IN_MILLIS, MINUTE_IN_MILLIS, THIRTY_SECS_IN_MILLIS, FLOATING_WIDGET_STATE } from '../../utils/constants';
import { isActionKey } from '../../utils/keyboard';
import { deleteStorage } from '../../utils/storage';

export default class FloatingActionButtonWidget extends Component {
  constructor(props) {
    super(props);

    const isDev = process.env.NODE_ENV === 'development';
    const urlParams = new URLSearchParams(window.location.search);
    const showDebug = isDev || urlParams.get('debugBambuser');

    this.state = {
      testGroup: props.bambuserLiveshoppingUid ? decideABTestGroup(props.bambuserLiveshoppingUid) : null,
      data: null,
      visible: false,
      dismissed: false,
      condensed: props.bambuserLiveshoppingFloatingCondensed === 'true' ? true : false,
      showDebug,
    };

    this._loadedLiveOnce = false;

    this.lastLoadStart = undefined;
    this.nextFetchTimeout = undefined;
    this._loadFloatingData = this._loadFloatingData.bind(this);
    this._announceFloatingWidgetPositionUpdate = this._announceFloatingWidgetPositionUpdate.bind(this);
    this._getFloatingWidgetPosition = this._getFloatingWidgetPosition.bind(this);
    this._navigateTo = this._navigateTo.bind(this);
    this._getTestSettingState = this._getTestSettingState.bind(this);
    trackingService.testingData = this._getTestSettingState();
  }

  _getTestSettingState() {
    const { testGroup, data } = this.state;
    const { testingEnabled } = data || {};
    return {
      testGroup,
      testingEnabled,
    };
  }

  componentDidMount() {
    this._loadFloatingData();
    const { showDebug } = this.state;

    const refetch = () => {
      showDebug && console.log('Page became visible/focused; will refetch in at most 30 seconds');
      this._loadFloatingData();
    };

    // set up listeners to refetch if the window gets focus or becomes visible
    // throttling fetches is handled inside _loadFloatingData
    try {
      window.top.addEventListener('focus', () => {
        refetch();
      });
    } catch (e) {
      if (showDebug) {
        console.log('Error adding focus event listener to top window', e);
      }
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    });

    // Listen for widget configuration
    window.addEventListener('message', (event) => {
      try {
        const { eventName, data } = JSON.parse(event.data);
        if (eventName !== `${WIDGET_EVENT_PREFIX}${WIDGET_EVENTS.CONFIGURE}` ||
          data.id !== this.props.id
        ) {
          return;
        }
        this.setState({ config: data.config });
      } catch (e) {
        // Ignore
      }
    });
  }

  _getFloatingWidgetPosition() {
    const { position } = this.state.data || {};
    return position;
  }

  _isEndedShowPlaylistAvailable() {
    const { enabled, endedShowPlaylist, isEnabledPlayEndedShows, show, version } = this.state.data || {};
    const widgetVersion = parseInt(version) || 1;
    const shouldPlayEndedShow = widgetVersion > 1 || isEnabledPlayEndedShows;

    return enabled && shouldPlayEndedShow && endedShowPlaylist?.length > 0 && show?.playerVersion > 1;
  }

  // Calculate based on state if widget should be presented
  _shouldPresentWidget() {
    const { data = {}, dismissed, testGroup } = this.state;
    const { enabled, show: { isLive } = {}, testingEnabled } = data;

    // Don't show widget for people in testGroup 0
    if (testingEnabled && testGroup !== undefined && testGroup === 0) return false;

    return enabled && (!!isLive || this._isEndedShowPlaylistAvailable()) && !dismissed;
  }

  _updateWidgetVisibility() {
    const shouldPresent = this._shouldPresentWidget();
    if (this.state.visible !== shouldPresent) {
      // Set state with slight delay to allow embed.js to set display property beforehand.
      // Otherwise the css transition would be ignored when fading in the widget.
      setTimeout(() => this.setState({ visible: shouldPresent }), 10);
    }

    return shouldPresent;
  }

  _resetCondensedState() {
    this.setState({ condensed: false });
    deleteStorage(FLOATING_WIDGET_STATE.CONDENSED);
  }

  // Fetch widget data every 60s instead of taking up firestore listeners.
  _loadFloatingData() {
    // if _loadFloatingData is called twice within 30 seconds, reschedule the next fetch so that
    // it will happen 30 seconds after the previous one
    if (this.lastLoadStart) {
      const msSinceLastFetch = Date.now() - this.lastLoadStart;
      if (msSinceLastFetch < THIRTY_SECS_IN_MILLIS) {
        // we just fetched, but we should fetch again after 30 seconds
        if (this.nextFetchTimeout) clearTimeout(this.nextFetchTimeout);
        this.nextFetchTimeout = setTimeout(this._loadFloatingData, THIRTY_SECS_IN_MILLIS - msSinceLastFetch);
        return;
      }
    }

    this.lastLoadStart = Date.now();

    const { backendBaseUrl, widgetId, themeId, channelLocale } = this.props;
    const { dismissed, showDebug } = this.state;
    const stateBackendBaseUrl = this.state?.data?.backendBaseUrl;

    if (dismissed) return;
    let url = `${stateBackendBaseUrl || backendBaseUrl}/floating/${widgetId}`;
    const queryParams = [];

    if (themeId) {
      queryParams.push(`themeId=${encodeURIComponent(themeId)}`);
    }

    if (channelLocale) {
      queryParams.push(`locale=${encodeURIComponent(channelLocale)}`);
    }

    if (queryParams.length) {
      url += `?${queryParams.join('&')}`;
    }

    const timeoutInSecs = 30;
    fetch(url, {
      signal: FetchTimeout(timeoutInSecs).signal,
    })
      .then((response) => {
        if (!response.ok) {
          const fetchError = new Error(`Error fetching floating widget data, status: ${response.status}`);
          throw fetchError;
        }
        return response.json();
      })
      .then((data) => {
        applyTheme(data.theme, 'FloatingActionButtonWidget');
        this.setState({ data }, () => {
          const { position, show = {}, allowCondensedWidget = true } = this.state.data || {};
          this._announceFloatingWidgetPositionUpdate(position);
          this._announceFloatingWidgetDisplay(this._updateWidgetVisibility());
          trackingService.testingData = this._getTestSettingState();
          if (show.isLive && !this._loadedLiveOnce) {
            this._loadedLiveOnce = true;
            const { showId, orgId } = show;
            trackingService.track({
              actionOrigin: WIDGET_EVENTS.ACTION_ORIGIN.FLOATING_ACTION_BUTTON,
              eventType: WIDGET_EVENTS.ON_LIVE_RESPONSE,
              orgId,
              widgetId,
              showId,
            });
          }

          const currentDate = new Date();
          const showScheduledDate = show.scheduledStartAt ? new Date(show.scheduledStartAt) : null;
          const timeDiffInHours = showScheduledDate ? (showScheduledDate - currentDate) / HOUR_IN_MILLIS : null;
          let timeToWaitInMillis = -1;

          if (!allowCondensedWidget && this.state.condensed) {
            showDebug && console.log('Condensed widget not allowed, uncondensing');
            this._resetCondensedState();
          }

          if (show.isArchived) {
            // Show is archived, no need to care about start time
            showDebug && console.log('Show is archived. Checking again in 10 minutes');
            timeToWaitInMillis = MINUTE_IN_MILLIS * 10;
            // Never allow condensed state for archived shows
            if (this.state.condensed) {
              showDebug && console.log('Show is archived, uncondensing');
              this._resetCondensedState();
            }
          } else if (show.isLive) {
            // Show is live, poll to know when it ends
            timeToWaitInMillis = THIRTY_SECS_IN_MILLIS;
            showDebug && console.log('Show is live, checking again in a 30s');
          } else if (timeDiffInHours === null) {
            showDebug && console.log('No scheduled time to start, checking again in a minute');
            timeToWaitInMillis = MINUTE_IN_MILLIS;
          } else if (timeDiffInHours >= -1) {
            if (timeDiffInHours <= 1) {
              // Show scheduled to start +/- an hour
              showDebug && console.log('Show scheduled to start in +/- an hour, checking again in 30s');
              timeToWaitInMillis = THIRTY_SECS_IN_MILLIS;
            } else {
              // Show scheduled to start in more than 1h
              showDebug && console.log('Show scheduled to start in more than 1h, checking again in 10 minutes');
              timeToWaitInMillis = MINUTE_IN_MILLIS * 10;
            }
          } else {
            showDebug && console.log('Show scheduled to start in the past? Checking again in 10 minutes');
            timeToWaitInMillis = MINUTE_IN_MILLIS * 10;
          }
          if (timeToWaitInMillis > 0) {
            showDebug && console.log(`Waiting ${timeToWaitInMillis / 1000}s...`);
            this.nextFetchTimeout = setTimeout(this._loadFloatingData, timeToWaitInMillis);
          }
        });
      })
      .catch((error) => {
        showDebug && console.log(error, showDebug);
      });
  }

  _getEffectiveShowId(isLive, showId) {
    return (isLive || !this._isEndedShowPlaylistAvailable()) ? showId : this.state.data.endedShowPlaylist[0];
  }


  onClickPlay() {
    const { id } = this.props;
    const { show: { embedBaseUrl, showId, isLive } = {}, destinationUrl } = this.state.data || {};
    const effectiveShowId = this._getEffectiveShowId(isLive, showId);
    // If widget data contains a destination then we send the user to that instead
    // This is done for practical reason to avoid presenting a "dumb" player without
    // actual player config and instead shuttle the user to a landing page where
    // we can (hopefully) autoplay the player with the correct config.
    // The destinationUrl has precedence over redirection.url.
    const redirection = this.state.config?.fab?.redirection ?? {};
    const href = destinationUrl || redirection.url;
    // change target if configured
    if (href) {
      this._navigateTo({
        showId: effectiveShowId,
        isLive,
        redirection: {
          ...redirection,
          url: href,
        },
      });
      // Do not show the player if we are redirecting
      return;
    }
    // Here we ask to hide widget by showing round "LIVE"-button only if it's live
    const data = {
      showId: effectiveShowId,
      embedBaseUrl,
      id,
      hideWidget: true,
      shouldCondense: isLive,
      isLive,
    };
    // Hide Floating component and consider it dismissed
    this.setState({
      visible: false,
      dismissed: true,
    });
    this._announcePostMessage(WIDGET_EVENTS.PLAY, data);
  }

  onClickClose() {
    const { id } = this.props;
    const { show: { isLive, showId } = {} } = this.state.data || {};
    const effectiveShowId = this._getEffectiveShowId(isLive, showId);
    // Hide Floating component and consider it dismissed
    this.setState({
      visible: false,
      dismissed: true,
    });
    this._announcePostMessage(WIDGET_EVENTS.FLOATING.DISMISS, { id, showId: effectiveShowId });
  }

  onKeyDownClose(e) {
    isActionKey(e) && onClickClose();
  }

  onClickCondense() {
    const { id } = this.props;
    // Change presentation state of Floating component
    this.setState({
      condensed: true,
    });
    this._announcePostMessage(WIDGET_EVENTS.FLOATING.CONDENSE, { id });
  }

  onKeydownCondense(e) {
    isActionKey(e) && onClickCondense();
  }

  onClickUncondense() {
    const { id } = this.props;
    // Change presentation state of Floating component
    this.setState({
      condensed: false,
    });
    this._announcePostMessage(WIDGET_EVENTS.FLOATING.EXPAND, { id });
  }

  // Navigate to destination
  _navigateTo({ showId, redirection, isLive } = {}) {
    const { id } = this.props;
    const data = { id, showId, redirection, isLive };
    this._announcePostMessage(WIDGET_EVENTS.FLOATING.NAVIGATE, data);
  }

  // Announce that the floating widget should change position
  _announceFloatingWidgetPositionUpdate(newPosition) {
    const { id } = this.props;
    const data = { position: newPosition, id };
    this._announcePostMessage(WIDGET_EVENTS.FLOATING.REPOSITION, data);
  }

  // Announce that the floating widget should change display
  _announceFloatingWidgetDisplay(present) {
    const { id } = this.props;
    const { show: { isLive, showId } = {} } = this.state.data || {};
    const effectiveShowId = this._getEffectiveShowId(isLive, showId);
    const showStatus = this._getShowStatus(effectiveShowId);
    const data = { present, showStatus, id, showId: effectiveShowId, allowDataRegionCookie: !!this.state.data?.general?.allowDataRegionCookie };
    this._announcePostMessage(WIDGET_EVENTS.FLOATING.PRESENT, data);
  }

  _announcePostMessage(eventName, data) {
    const payload = JSON.stringify({
      eventName: `${WIDGET_EVENT_PREFIX}${eventName}`,
      data,
      state: this.state?.data,
    });
    window.parent.postMessage(payload, '*');
  }

  _getShowId() {
    const { isLive, showId: liveShowId } = this.state.data?.show || {};
    const endedShowId = this._isEndedShowPlaylistAvailable() && this.state.data?.endedShowPlaylist?.[0];

    return isLive ? liveShowId : (endedShowId || liveShowId);
  }

  _getShowStatus(effectiveShowId) {
    const { show: { showId, isLive, isArchived, scheduledStartAt } = {} } = this.state.data || {};
    // This means the show id is from the endedShowPlaylist and thus the show is recorded
    if (effectiveShowId !== showId) {
      return 'recorded';
    }
    // Otherwise, based on the featured show, it can be 'live', 'recorded', 'scheduled', or 'upcoming'
    return isLive
      ? 'live'
      : isArchived
        ? 'recorded'
        : scheduledStartAt
          ? 'scheduled'
          : 'upcoming';
  }

  _shouldUseLightWeightPlayer() {
    const { isLive } = this.state.data?.show || {};

    return Boolean(!isLive && this._isEndedShowPlaylistAvailable());
  }

  _allowCondense() {
    // This is currently the only case where to need the condensed state
    return this.state?.data?.allowCondensedWidget && !this._shouldUseLightWeightPlayer();
  }

  render() {
    const { widgetId } = this.props;
    const { condensed, showDebug, visible } = this.state;
    const show = this.state.data ? this.state.data.show : null;
    const previewDuration = this.state.data ? this.state.data.previewDuration : null;
    const allowCondense = this._allowCondense();

    if (!show && !this._isEndedShowPlaylistAvailable()) {
      return null;
    }

    return (
      <FloatingActionButton
        isCondensed={condensed}
        isVisible={visible}
        widgetId={widgetId}
        previewDuration={previewDuration}
        showDebug={showDebug}
        {...show}
        showId={this._getShowId()}
        shouldUseLightWeightPlayer={this._shouldUseLightWeightPlayer()}
        theme={theme}
        onClickPlay={condensed ? this.onClickUncondense.bind(this) : this.onClickPlay.bind(this)}
        onKeyDownCondense={allowCondense && this.onKeydownCondense.bind(this)}
        onClickCondense={allowCondense && this.onClickCondense.bind(this)}
        onKeyDownClose={!allowCondense && this.onKeyDownClose.bind(this)}
        onClickClose={!allowCondense && this.onClickClose.bind(this)}
      />
    );
  }
}
